import { AGENT_INTERCEPTION_IGNORED_SELECTORS } from '../constants';

/**
 * Bare modifier presses. A modifier keydown fires on its OWN, before the combo it
 * begins — pressing `Alt+I` emits `keydown{key:'Alt'}` and only then
 * `keydown{key:'i', altKey:true}`.
 *
 * So a guard that treats any keydown as taking over the order fires on the Alt, and
 * the shortcut's own `stopPropagation()` never gets the chance to prevent it. Every
 * Alt shortcut in the app is affected — `Alt+1…9` intents, `Alt+A`/`Alt+D`,
 * `Alt+/`, and `Alt+I` — which is why this belongs in the shared guard and not in
 * any one hotkey.
 *
 * Holding a modifier is not an edit. The keystroke it prefixes may well be, and that
 * one still arrives and is judged on its own.
 */
const MODIFIER_KEYS = ['Alt', 'Control', 'Shift', 'Meta', 'AltGraph'];

/**
 * True when an event must NOT raise the "would you like to take over?" prompt.
 *
 * Shared by AgentInterceptionConfirm (HITL 3.0) and AIStatusBar (legacy) so the two
 * cannot drift — their dialog logic is documented as kept in lockstep, and it was
 * previously copy-pasted.
 *
 * Two reasons to ignore an event:
 * 1. It is a bare modifier keydown (above).
 * 2. Its target sits inside an allowlisted subtree — chrome, the transcript, the
 *    issue-report panel. Matched with `closest()`, so the marker need only be an
 *    ANCESTOR of the real target.
 */
export const isInterceptionIgnoredEvent = (event: Event): boolean => {
  const e = event as KeyboardEvent;
  const isBareModifier =
    event.type === 'keydown' && MODIFIER_KEYS.includes(e.key);
  const target = event.target as HTMLElement | null;
  const matchedSelector =
    target && typeof target.closest === 'function'
      ? AGENT_INTERCEPTION_IGNORED_SELECTORS.find((selector) =>
          target.closest(selector)
        )
      : undefined;

  return isBareModifier || Boolean(matchedSelector);
};

export default isInterceptionIgnoredEvent;

/**
 * The single keydown/mousedown the take-over guard most recently prompted for.
 *
 * Exists so `useIntentHotkeys` can tell whether the very keystroke it is about to act
 * on was the one that raised the prompt — and stay silent if so. Clicking a pill gets
 * that for free: the dialog opens on `mousedown`, so its backdrop is in the DOM
 * before `mouseup` and the pill never receives the `click`. A keydown is one event
 * with nothing to intercept it, so the keyboard has to ask.
 *
 * Keyed on the EVENT rather than on "is a dialog open", which was the first attempt
 * and is wrong twice over: MUI keeps the dialog mounted through its exit transition,
 * so a press moments after confirming would be swallowed; and any unrelated modal
 * would suppress intents for as long as it stayed up.
 */
let lastPromptedEvent: Event | null = null;

export const markInterceptionPrompted = (event: Event): void => {
  lastPromptedEvent = event;
};

export const wasInterceptionPromptedFor = (event: Event): boolean =>
  lastPromptedEvent === event;

/** Test seam — module state would otherwise leak between cases. */
export const resetInterceptionPromptTracking = (): void => {
  lastPromptedEvent = null;
};
